import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { cloneElement, useEffect, useId, useMemo, useState, type ReactElement } from "react";
import { useCart, cartTotals, isIncluded, suggestPackage } from "@/lib/cart";
import { getPackageStyle } from "@/lib/package-styles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Disclaimer } from "@/components/site/Disclaimer";
import { money } from "@/lib/format";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { fetchPackages, fetchServices, fetchSettings } from "@/lib/queries";
import { Sparkles, Plus, Zap, ArrowRight, ArrowLeft } from "lucide-react";
import { trackCheckoutContinueToStripe } from "@/lib/analytics";

export const Route = createFileRoute("/checkout")({
  head: () => ({ meta: [{ title: "Revisión de tu pedido — Truck Company Center" }, { name: "robots", content: "noindex" }] }),
  component: Checkout,
});

const URGENT_FEE = 149;

function Checkout() {
  const nav = useNavigate();
  const { items, clear } = useCart();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: fetchSettings });
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: fetchServices });
  const { data: packages = [] } = useQuery({ queryKey: ["packages"], queryFn: fetchPackages });
  const depositEnabled = !!settings?.features?.deposit_enabled;
  const depositPct = Number(settings?.features?.deposit_percent ?? 30);

  const [step, setStep] = useState<"suggest" | "review">("suggest");
  const [urgent, setUrgent] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [canceled, setCanceled] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("canceled") === "1") {
      setCanceled(true);
      setStep("review");
      toast.warning("Pago cancelado. Puedes revisar tu pedido e intentarlo nuevamente.");
    }
  }, []);

  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", preferred_contact: "WhatsApp",
    company_name: "", state: "FL",
    company_exists: "no", operating: "interstate",
    has_dot: "no", has_mc: "no",
    needs_central: "yes", needs_super: "yes",
    notes: "",
  });
  const [pay, setPay] = useState<"full" | "deposit">("full");
  const [submitting, setSubmitting] = useState(false);

  // Pre-fill from logged-in user's customer record
  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;
      const { data: customer } = await supabase
        .from("customers")
        .select("full_name,email,phone,preferred_contact,company_name,state")
        .eq("user_id", session.user.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      setForm((f) => ({
        ...f,
        full_name: customer?.full_name ?? f.full_name,
        email: customer?.email ?? session.user.email ?? f.email,
        phone: customer?.phone ?? f.phone,
        preferred_contact: customer?.preferred_contact ?? f.preferred_contact,
        company_name: customer?.company_name ?? f.company_name,
        state: customer?.state ?? f.state,
      }));
    })();
  }, []);

  const { subtotal: baseSubtotal } = cartTotals(items);
  const total = baseSubtotal + (urgent ? URGENT_FEE : 0);
  const due = pay === "deposit" && depositEnabled ? Math.round(total * depositPct) / 100 : total;

  // ---------- Smart suggestions ----------
  const suggestions = useMemo(() => {
    if (items.length === 0) return [];
    const out: { reason: string; service: any }[] = [];
    const svcBySlug = new Map(services.map((s) => [s.slug, s]));
    const hasService = (slug: string) =>
      items.some((i) => i.type === "service" && i.slug === slug) ||
      items.some((i) => {
        if (i.type !== "package") return false;
        const svc = svcBySlug.get(slug);
        return svc ? i.includedServiceIds.includes(svc.id) : false;
      });
    const hasPackageSlug = (slug: string) =>
      items.some((i) => i.type === "package" && i.slug === slug);

    function suggest(slug: string, reason: string) {
      const s = svcBySlug.get(slug);
      if (!s) return;
      if (s.price_type === "variable") return;
      if (!s.active) return;
      if (hasService(slug)) return;
      if (out.some((o) => o.service.slug === slug)) return;
      out.push({ service: s, reason });
    }

    if (hasService("bol") && !hasService("logo")) {
      suggest("logo", "Recomendado: tu BOL queda más profesional con tu logo.");
    }
    if (hasPackageSlug("base-florida")) {
      suggest("usdot-mc-requisitos", "Si vas a interstate, vas a necesitar tu USDOT y MC.");
      suggest("central-dispatch", "Apertura de cuenta en Central Dispatch para empezar a buscar cargas.");
    }
    if (hasPackageSlug("operacion-lista")) {
      suggest("clase-central", "Aprende a usar Central Dispatch desde el primer día.");
      suggest("tarjetas-negocio", "Tarjetas de negocio para presentarte serio.");
    }
    if (!items.some((i) => i.type === "package")) {
      // standalone services — suggest the cheapest package that overlaps
      const serviceItemIds = items.filter((i) => i.type === "service").map((i) => i.id);
      const best = packages
        .map((p) => {
          const overlap = serviceItemIds.filter((id) => p.service_ids.includes(id)).length;
          const standaloneTotal = items
            .filter((i) => i.type === "service" && p.service_ids.includes(i.id))
            .reduce((a, i) => a + Number(i.price), 0);
          return { p, overlap, saves: standaloneTotal - Number(p.price) };
        })
        .filter((x) => x.overlap >= 2 && x.saves > 0)
        .sort((a, b) => b.saves - a.saves)[0];
      if (best) {
        out.unshift({
          service: { __pkg: true, ...best.p, saves: best.saves },
          reason: `Si llevas estos servicios sueltos, el paquete ${best.p.name} te saldría más barato.`,
        });
      }
    } else {
      // Caso 2: paquete inferior + extras → ¿conviene un paquete superior?
      const upgrade = suggestPackage(items, packages);
      if (upgrade && upgrade.mode === "upgrade-from-package") {
        out.unshift({
          service: { __pkg: true, ...upgrade.pkg, saves: upgrade.saves },
          reason: `Con tu paquete actual + los extras que añadiste, ${upgrade.pkg.name} te sale más barato.`,
        });
      }
    }
    return out.slice(0, 4);
  }, [items, services, packages]);

  const update = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const cart = useCart();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (items.length === 0) { toast.error("Tu carrito está vacío"); return; }
    if (!form.full_name || !form.email) { toast.error("Completa nombre y email"); return; }
    if (!acceptedTerms) { toast.error("Debes aceptar los términos para continuar"); return; }
    setSubmitting(true);
    // Hard timeout so the button never stays stuck on "Redirigiendo..." if the
    // network or upstream Stripe call hangs.
    const ctrl = new AbortController();
    const timeoutId = window.setTimeout(() => ctrl.abort(), 25_000);
    try {
      const notesWithFlag = urgent
        ? `[URGENTE +$${URGENT_FEE}] ${form.notes}`.trim()
        : form.notes;
      const res = await fetch("/api/create-checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: ctrl.signal,
        body: JSON.stringify({
          customer: {
            full_name: form.full_name, email: form.email, phone: form.phone,
            preferred_contact: form.preferred_contact, company_name: form.company_name, state: form.state,
          },
          items: items.map((i) => ({ type: i.type, id: i.id })),
          operational: {
            company_exists: form.company_exists, operating: form.operating,
            has_dot: form.has_dot, has_mc: form.has_mc,
            needs_central: form.needs_central, needs_super: form.needs_super,
            payment_choice: pay, deposit_percent: pay === "deposit" ? depositPct : 100,
          },
          notes: notesWithFlag,
          urgent,
          legal_acceptance: {
            accepted_service_agreement: true,
            accepted_limited_authorization: true,
            accepted_privacy_data_consent: true,
            accepted_refund_policy: true,
            source_route: "checkout",
            user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
          },
        }),
      });
      let data: any = null;
      try { data = await res.json(); } catch { /* non-JSON response */ }
      if (!res.ok || !data?.url) {
        throw new Error(
          data?.error ??
          (res.status === 500 ? "El servicio de pago no está disponible. Intenta de nuevo en unos minutos." :
           res.status === 429 ? "Demasiadas solicitudes. Intenta de nuevo más tarde." :
           "No se pudo iniciar el pago")
        );
      }
      trackCheckoutContinueToStripe(due);
      
      window.location.href = data.url;
    } catch (err: any) {
      const msg = err?.name === "AbortError"
        ? "El pago tardó demasiado en responder. Verifica tu conexión e intenta de nuevo."
        : err?.message ?? "Error al iniciar el pago";
      toast.error(msg);
      setSubmitting(false);
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 md:px-6">
        <h1 className="font-display text-3xl font-semibold text-foreground">Tu carrito está vacío</h1>
        <p className="mt-2 text-muted-foreground">Agrega un paquete o servicios para continuar.</p>
        <Link to="/paquetes"><Button className="mt-6 bg-primary text-primary-foreground hover:bg-primary/90">Ver paquetes</Button></Link>
      </div>
    );
  }

  // ============== STEP 1: SUGGESTIONS ==============
  if (step === "suggest") {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12 md:px-6">
        <div className="text-xs uppercase tracking-[0.2em] text-label-accent">Paso 1 de 2 · Antes de continuar</div>
        <h1 className="mt-3 font-display text-3xl font-semibold text-foreground md:text-4xl">
          {suggestions.length > 0 ? "Quizás también te interese esto" : "Todo listo para revisar tu pedido"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {suggestions.length > 0
            ? "Sugerencias basadas en lo que ya elegiste. Puedes ignorarlas si no aplica a tu caso."
            : "Tu carrito ya cubre los servicios principales. Puedes continuar con la revisión."}
        </p>

        {suggestions.length > 0 && (
          <div className="mt-8 space-y-3">
            {suggestions.map((sg, idx) => {
              const s = sg.service;
              if (s.__pkg) {
                const ps = getPackageStyle(s.slug);
                return (
                  <div key={`pkg-${s.id}`} className={`rounded-xl border ${ps.cardBorder} bg-surface p-5`}>
                    <div className="flex items-start gap-3">
                      <Sparkles className={`mt-0.5 h-5 w-5 shrink-0 ${ps.iconClass}`} />
                      <div className="flex-1">
                        <div className={`text-[11px] uppercase tracking-wider ${ps.badgeText}`}>Te conviene un paquete</div>
                        <div className="mt-1 font-display text-lg font-semibold text-foreground">{s.name}</div>
                        <p className="mt-1 text-sm text-muted-foreground">{sg.reason}</p>
                        <p className="mt-2 text-sm text-foreground">
                          Precio del paquete: <span className="font-semibold">{money(s.price)}</span> · Ahorras <span className={`font-semibold ${ps.badgeText}`}>{money(s.saves)}</span>
                        </p>
                      </div>
                      <Button
                        size="sm"
                        className={ps.buttonClass}
                        onClick={() =>
                          cart.addPackage({
                            id: s.id, slug: s.slug, name: s.name,
                            price: Number(s.price),
                            includedServiceIds: [...(s.service_ids ?? []), ...(s.bonus_service_ids ?? [])],
                          })
                        }
                      >
                        Cambiar al paquete
                      </Button>
                    </div>
                  </div>
                );
              }
              const inCart = items.some((it) => it.type === "service" && it.id === s.id);
              const included = isIncluded(s.id, items);
              return (
                <div key={`svc-${s.id}-${idx}`} className="rounded-xl border border-hairline bg-surface p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] uppercase tracking-wider text-label-accent">{s.category}</div>
                      <div className="mt-1 font-medium text-foreground">{s.name}</div>
                      <p className="mt-1 text-sm text-muted-foreground">{sg.reason}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className="font-display text-lg font-semibold text-foreground">{money(s.price)}</span>
                      {included ? (
                        <span className="rounded bg-success/15 px-2 py-1 text-[11px] font-medium text-[oklch(0.78_0.16_155)]">Ya incluido</span>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={inCart}
                          className="border-hairline"
                          onClick={() => cart.addService({ id: s.id, slug: s.slug, name: s.name, price: Number(s.price) })}
                        >
                          {inCart ? "Añadido ✓" : <><Plus className="mr-1 h-3 w-3" /> Añadir</>}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-10 flex flex-col items-center justify-between gap-3 border-t border-hairline pt-6 sm:flex-row">
          <Link to="/servicios" className="text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="mr-1 inline h-3 w-3" /> Seguir agregando servicios
          </Link>
          <Button
            size="lg"
            onClick={() => setStep("review")}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            Continuar a la revisión <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  // ============== STEP 2: REVIEW & SUBMIT ==============
  return (
    <div className="mx-auto max-w-7xl px-4 py-12 md:px-6">
      <div className="text-xs uppercase tracking-[0.2em] text-label-accent">Paso 2 de 2 · Revisión</div>
      <div className="mt-2 flex items-center justify-between">
        <h1 className="font-display text-3xl font-semibold text-foreground md:text-4xl">Revisión de tu pedido</h1>
        <button type="button" onClick={() => setStep("suggest")} className="text-xs text-muted-foreground hover:text-foreground">
          <ArrowLeft className="mr-1 inline h-3 w-3" /> Volver a sugerencias
        </button>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">Revisa tus datos y el resumen de tu pedido. Al continuar, podrás completar el pago de forma segura.</p>
      {canceled && (
        <div className="mt-4 rounded-md border border-yellow-500/40 bg-yellow-500/10 p-3 text-sm text-foreground">
          Pago cancelado. Puedes revisar tu pedido e intentarlo nuevamente.
        </div>
      )}
      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_380px]">
        <form onSubmit={submit} className="space-y-8">
          <Section title="Datos de contacto">
            <Field label="Nombre completo *"><Input className="bg-surface border-hairline" required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} /></Field>
            <Field label="Email *"><Input className="bg-surface border-hairline" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} /></Field>
            <Field label="Teléfono"><Input className="bg-surface border-hairline" value={form.phone} onChange={(e) => update("phone", e.target.value)} /></Field>
            <Field label="Contacto preferido">
              <select className="h-9 w-full rounded-md border border-hairline bg-surface px-3 text-sm" value={form.preferred_contact} onChange={(e) => update("preferred_contact", e.target.value)}>
                <option>WhatsApp</option><option>Llamada</option><option>Email</option>
              </select>
            </Field>
            <Field label="Nombre de la compañía (si tienes)"><Input className="bg-surface border-hairline" value={form.company_name} onChange={(e) => update("company_name", e.target.value)} /></Field>
            <Field label="Estado"><Input className="bg-surface border-hairline" value={form.state} onChange={(e) => update("state", e.target.value)} /></Field>
          </Section>

          <Section title="Operación">
            <Radio label="¿Tu compañía ya existe?" value={form.company_exists} onChange={(v) => update("company_exists", v)} options={[["yes","Sí"],["no","No"]]} />
            <Radio label="¿Operación intrastate o interstate?" value={form.operating} onChange={(v) => update("operating", v)} options={[["intrastate","Intrastate"],["interstate","Interstate"]]} />
            <Radio label="¿Ya tienes USDOT?" value={form.has_dot} onChange={(v) => update("has_dot", v)} options={[["yes","Sí"],["no","No"]]} />
            <Radio label="¿Ya tienes MC?" value={form.has_mc} onChange={(v) => update("has_mc", v)} options={[["yes","Sí"],["no","No"]]} />
            <Radio label="¿Necesitas Central Dispatch?" value={form.needs_central} onChange={(v) => update("needs_central", v)} options={[["yes","Sí"],["no","No"]]} />
            <Radio label="¿Necesitas Super Dispatch?" value={form.needs_super} onChange={(v) => update("needs_super", v)} options={[["yes","Sí"],["no","No"]]} />
            <Field label="Notas / situación especial">
              <Textarea className="bg-surface border-hairline" rows={3} value={form.notes} onChange={(e) => update("notes", e.target.value)} />
            </Field>
          </Section>

          {/* Urgent toggle */}
          <button
            type="button"
            onClick={() => setUrgent((v) => !v)}
            className={`flex w-full items-start gap-4 rounded-xl border p-5 text-left transition ${urgent ? "border-primary bg-primary/10" : "border-hairline bg-surface hover:bg-surface-2"}`}
          >
            <div className={`mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-md ${urgent ? "bg-primary text-primary-foreground" : "bg-surface-2 text-primary"}`}>
              <Zap className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground">Quiero que mi proceso entre como urgente</span>
                <span className="rounded bg-primary/15 px-2 py-0.5 text-[11px] font-semibold text-primary">+{money(URGENT_FEE)}</span>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                Prioridad interna para empezar tu proceso más rápido. No garantiza tiempos de terceros (USDOT, MC, plataformas, seguros).
              </p>
            </div>
            <div className={`mt-1 h-6 w-11 shrink-0 rounded-full p-0.5 transition ${urgent ? "bg-primary" : "bg-surface-2 border border-hairline"}`}>
              <div className={`h-5 w-5 rounded-full bg-background transition ${urgent ? "translate-x-5" : ""}`} />
            </div>
          </button>

          {depositEnabled && (
            <Section title="Forma de pago">
              <div className="grid gap-3 sm:grid-cols-2">
                <button type="button" onClick={() => setPay("full")} className={`rounded-md border p-4 text-left ${pay==="full" ? "border-primary bg-primary/10" : "border-hairline bg-surface"}`}>
                  <div className="font-medium text-foreground">Pagar completo</div>
                  <div className="mt-1 text-sm text-muted-foreground">{money(total)} hoy</div>
                </button>
                <button type="button" onClick={() => setPay("deposit")} className={`rounded-md border p-4 text-left ${pay==="deposit" ? "border-primary bg-primary/10" : "border-hairline bg-surface"}`}>
                  <div className="font-medium text-foreground">Depósito {depositPct}%</div>
                  <div className="mt-1 text-sm text-muted-foreground">{money(due)} hoy, balance después</div>
                </button>
              </div>
            </Section>
          )}

          <Disclaimer />

          <label className="flex items-start gap-3 rounded-xl border border-hairline bg-surface p-4 text-sm text-foreground">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-primary"
            />
            <span className="leading-relaxed">
              I agree to the{" "}
              <a href="/legal/service-agreement" target="_blank" rel="noreferrer" className="text-primary underline hover:no-underline">Service Agreement</a>,{" "}
              <a href="/legal/limited-authorization" target="_blank" rel="noreferrer" className="text-primary underline hover:no-underline">Limited Authorization</a>,{" "}
              <a href="/refund-policy" target="_blank" rel="noreferrer" className="text-primary underline hover:no-underline">Refund Policy</a>,{" "}
              <a href="/privacy-policy" target="_blank" rel="noreferrer" className="text-primary underline hover:no-underline">Privacy Policy</a>, and{" "}
              <a href="/terms" target="_blank" rel="noreferrer" className="text-primary underline hover:no-underline">Terms</a>, and understand that {`Truck Company Center`} provides administrative assistance only and does not guarantee approvals by government agencies, platforms, insurers, or third parties. The full electronic signature will be collected with the intake form after payment.
            </span>
          </label>

          <Button type="submit" size="lg" disabled={submitting || !acceptedTerms} className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
            {submitting ? "Redirigiendo al pago..." : "Continuar al pago seguro"}
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            Al continuar, serás dirigido al pago seguro. Iniciaremos tu proceso después de recibir el pago y la información requerida.
          </p>
        </form>

        <aside className="lg:sticky lg:top-20 lg:self-start">
          <div className="rounded-xl border border-hairline bg-surface p-6">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Resumen</div>
            <ul className="mt-4 space-y-2 text-sm">
              {items.map((i) => (
                <li key={i.id} className="flex justify-between text-foreground">
                  <span className="truncate pr-2">{i.name}</span>
                  <span>{money(i.price)}</span>
                </li>
              ))}
              {urgent && (
                <li className="flex justify-between text-primary">
                  <span className="flex items-center gap-1.5"><Zap className="h-3.5 w-3.5" /> Proceso urgente</span>
                  <span>+{money(URGENT_FEE)}</span>
                </li>
              )}
            </ul>
            <div className="mt-4 border-t border-hairline pt-4 text-sm">
              <Row k="Subtotal" v={money(baseSubtotal)} />
              {urgent && <Row k="Urgente" v={`+${money(URGENT_FEE)}`} />}
              <div className="mt-2 flex justify-between text-base font-semibold text-foreground"><span>Total</span><span>{money(total)}</span></div>
              {pay === "deposit" && depositEnabled && (
                <div className="mt-2 flex justify-between text-sm text-primary"><span>Hoy ({depositPct}%)</span><span>{money(due)}</span></div>
              )}
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground">El total mostrado corresponde a los servicios seleccionados. Algunos fees oficiales fijos pueden estar incluidos según el servicio. Costos variables como seguro, suscripciones, impresión, ropa, bordado, envío u otros cargos externos se cotizan o pagan aparte si aplican. No garantizamos aprobaciones de terceros.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-hairline bg-surface p-6">
      <div className="text-xs uppercase tracking-[0.2em] text-label-accent">{title}</div>
      <div className="mt-4 grid gap-4 md:grid-cols-2">{children}</div>
    </div>
  );
}
function Field({ label, children }: { label: string; children: ReactElement<{ id?: string }> }) {
  const id = useId();
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs text-muted-foreground">{label}</Label>
      {cloneElement(children, { id })}
    </div>
  );
}
function Radio({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: [string,string][] }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="flex gap-2">
        {options.map(([v,l]) => (
          <button type="button" key={v} onClick={() => onChange(v)}
            className={`flex-1 rounded-md border px-3 py-2 text-sm ${value===v ? "border-primary bg-primary/10 text-foreground" : "border-hairline bg-surface text-muted-foreground"}`}>{l}</button>
        ))}
      </div>
    </div>
  );
}
function Row({ k, v }: { k: string; v: string }) {
  return <div className="flex justify-between text-muted-foreground"><span>{k}</span><span>{v}</span></div>;
}
